import {
  Box,
  Button,
  IconButton,
  TextareaAutosize,
  Typography,
} from "@material-ui/core";
import {
  COMMENTARY_FILTERS,
  useGenerateCommentaryMutation,
  useSavePromptMutation,
} from "~/store/mystore/notes.redux";
import { EditorState, convertFromRaw } from "draft-js";
import React, { useEffect, useRef, useState } from "react";

import ActionIconButton from "../buttons/actionIconButton";
import AiInsightsIcon from "~/icons/aiInsightsIcon";
import PanelLoading from "../loadingIndicator/panelLoadingIndicator";
import RetryIcon from "~/icons/retryIcon";
import { SaveOutlined } from "@material-ui/icons";
import Typewriter from "./typewriter";
import { markdownToDraft } from "markdown-draft-js";
import styled from "styled-components";
import { useCommentaryContext } from "~/hooks/useCommentaryContext";
import { useTranslation } from "react-i18next";

const PromptContainer = styled(Box)`
  background-color: #ffffff;
  border: 1px solid #e0e0e0;
  border-radius: 8px;
  width: 100%;
  margin: 0 auto;
`;

const PromptHeader = styled(Box)`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 1rem 1.5rem;
  border-bottom: 1px solid #e0e0e0;
`;

const PromptContent = styled(Box)`
  padding: 1.5rem;
`;

const StyledTextarea = styled(TextareaAutosize)`
  width: 100%;
  min-height: 120px;
  padding: 1rem;
  border: 1px solid #e0e0e0;
  border-radius: 4px;
  font-family: "Roboto", "Helvetica", "Arial", sans-serif;
  font-size: 0.875rem;
  line-height: 1.5;
  resize: none;
  outline: none;
  background-color: #ffffff;

  &:focus {
    border-color: #2196f3;
  }
`;

const ActionBar = styled(Box)`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-top: 1rem;
`;

const ButtonGroup = styled(Box)`
  display: flex;
  gap: 0.75rem;
  align-items: center;
`;

const GenerateButton = styled(Button)`
  background-color: #2196f3;
  color: white;
  font-weight: 600;
  font-size: 0.75rem;
  letter-spacing: 0.5px;
  padding: 0.5rem 1.5rem;

  &:hover {
    background-color: #1976d2;
  }
`;

const CloseButton = styled(Button)`
  color: #666666;
  font-weight: 500;
  font-size: 0.75rem;
  letter-spacing: 0.5px;

  &:hover {
    background-color: rgba(0, 0, 0, 0.04);
  }
`;

const CreditsContainer = styled(Box)`
  display: flex;
  align-items: center;
  gap: 0.25rem;
`;

const ResetButton = styled(Button)`
  color: #666666;
  font-size: 0.75rem;
  font-weight: 500;
  letter-spacing: 0.5px;
  min-width: auto;
  padding: 0.25rem 0.5rem;

  &:hover {
    background-color: rgba(0, 0, 0, 0.04);
  }
`;

const CommentaryContainer = styled(Box)`
  width: 100%;
  margin: 1rem 0;
`;

const CommentaryHeader = styled(Box)`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 5px;
`;

const CommentaryContent = styled.div`
  padding: 1.5rem;
  border: 1px solid #e0e0e0;
  color: black;
  border-radius: 4px;
  background-color: ${({ theme }) => theme.palette.border.main};
  max-height: 200px;
  overflow-y: auto;
  overflow-x: hidden;
  word-wrap: break-word;
  word-break: break-word;
  overflow-wrap: break-word;
  white-space: pre-wrap;
  hyphens: auto;
  line-height: 1.5;
  font-family: "Roboto", "Helvetica", "Arial", sans-serif;
  font-size: 0.875rem;
  width: 100%;
  box-sizing: border-box;
`;

const CreditText = styled(Typography)`
  color: #757575;
  font-size: 0.875rem;
`;

type CommentaryGeneratorProps = {
  autoMode: boolean;
  setAutoMode: React.Dispatch<React.SetStateAction<boolean>>;
  setEditorState: (state: EditorState) => void;
  initialCredits?: number | null;
  cachedPrompt?: string;
  setEditMode: React.Dispatch<React.SetStateAction<boolean>>;
  filter?: COMMENTARY_FILTERS;
  tag?: string;
};

const CommentaryGenerator: React.FC<CommentaryGeneratorProps> = ({
  autoMode,
  setAutoMode,
  initialCredits,
  cachedPrompt,
  setEditorState,
  setEditMode,
  filter,
  tag,
}) => {
  const { t } = useTranslation();

  const [promptText, setPromptText] = useState<string>(cachedPrompt ?? "");
  const commentaryRef = useRef<HTMLDivElement>(null);

  const [typeStatus, setTypeStatus] = useState<"init" | "typing" | "done">(
    "init"
  );
  const [remainingCredits, setRemainingCredits] = useState<number | "-">(
    initialCredits ?? "-"
  );
  const [usedCredit, setUsedCredit] = useState<number>(0);

  const [commentaryTitle, setCommentaryTitle] = useState<string>("");
  const [commentary, setCommentary] = useState<string>("");

  const rawData = markdownToDraft(commentaryTitle + " : " + commentary || "");

  const [generateCommentary, { isLoading, data: commentaryResponse }] =
    useGenerateCommentaryMutation();

  const [savePrompt] = useSavePromptMutation();

  const handleReset = () => {
    setPromptText("");
    setCommentaryTitle("");
    setCommentary("");
  };

  const context = useCommentaryContext(filter ?? COMMENTARY_FILTERS.OVERVIEW);

  const handleSavePrompt = async () => {
    await savePrompt({ newPrompt: promptText });
  };

  const handleKeep = async () => {
    //set editor state
    setEditorState(EditorState.createWithContent(convertFromRaw(rawData)));
    //back to normal mode
    setAutoMode(false);
    setEditMode(true);
  };

  const handleGenerate = async () => {
    await generateCommentary({
      context,
      prompt: promptText,
    });
    if (!commentaryResponse) {
      return;
    }
  };

  const handleClose = () => {
    setAutoMode(!autoMode);
  };

  const handleRefresh = async () => {
    setCommentaryTitle("");
    setCommentary("");
    await generateCommentary({
      context,
      prompt: promptText,
      refresh: true,
    });
  };

  useEffect(() => {
    if (commentaryResponse) {
      setCommentaryTitle(commentaryResponse.instanceData.commentaryTitle);
      setCommentary(commentaryResponse.instanceData.commentaryDetail);
      setUsedCredit(commentaryResponse.instanceData.totalCreditUsage);
      const remainCredits = commentaryResponse.instanceData.newUserCredits;
      setRemainingCredits(remainCredits);
    }
  }, [commentaryResponse]);

  useEffect(() => {
    if (commentaryRef.current) {
      commentaryRef.current.scrollTop = commentaryRef.current.scrollHeight;
    }
  }, [commentaryRef]);

  return (
    <PromptContainer>
      <PromptHeader>
        <Typography variant="h2" color="textSecondary">
          {t("reportComments.generatorPrompt")}
        </Typography>
        <Box display="flex" alignItems="center">
          <IconButton size="small" onClick={handleSavePrompt}>
            <SaveOutlined fontSize="small" />
          </IconButton>
          <ResetButton onClick={handleReset}>
            {t("reportComments.generatorRest")}
          </ResetButton>
        </Box>
      </PromptHeader>

      <PromptContent>
        <StyledTextarea
          value={promptText}
          onChange={(e) => setPromptText(e.target.value)}
          placeholder="Enter your prompt here..."
        />

        {isLoading ? (
          <PanelLoading />
        ) : commentaryResponse ? (
          <CommentaryContainer>
            <CommentaryHeader>
              <Typography variant="h5" color="textSecondary">
                {t("reportComments.generatorCommentary")}
              </Typography>
              <CreditText>
                {usedCredit}&nbsp;
                {t("reportComments.generatorCreditsUsed")}
              </CreditText>
            </CommentaryHeader>
            <CommentaryContent ref={commentaryRef}>
              <Typography variant="h4" style={{ fontWeight: "bold" }}>
                {commentaryTitle} :
              </Typography>
              <Typewriter
                text={commentary}
                delay={15}
                scrollRef={commentaryRef}
                setTypeStatus={setTypeStatus}
              />
            </CommentaryContent>
          </CommentaryContainer>
        ) : undefined}

        <ActionBar>
          {typeStatus !== "done" ? (
            <ButtonGroup>
              <GenerateButton
                onClick={handleGenerate}
                disabled={typeStatus === "typing"}
              >
                {t("reportComments.generatorGenerate")}
              </GenerateButton>
              <CloseButton
                onClick={handleClose}
                disabled={typeStatus === "typing"}
              >
                {t("generic.closeButton")}
              </CloseButton>
            </ButtonGroup>
          ) : (
            <ButtonGroup>
              <GenerateButton onClick={handleKeep}>
                {t("reportComments.generatorKeep")}
              </GenerateButton>

              <ActionIconButton onClick={handleRefresh}>
                <RetryIcon />
              </ActionIconButton>
              <CloseButton onClick={handleClose}>
                {t("generic.closeButton")}
              </CloseButton>
            </ButtonGroup>
          )}

          <CreditsContainer>
            <Typography variant="body2" color="textSecondary">
              {t("reportComments.generatorRemainingCredits")}
            </Typography>
            <Typography
              variant="body2"
              style={{ color: "#2196f3", fontWeight: 600 }}
            >
              {remainingCredits.toLocaleString()}
            </Typography>
            <AiInsightsIcon />
          </CreditsContainer>
        </ActionBar>
      </PromptContent>
    </PromptContainer>
  );
};

export default CommentaryGenerator;
